import dayjs from 'dayjs';
import { valueEmpty } from 'helpers';
import { createDurationOption } from '../utils';

export const getBoatAvailableOptions = (selectedBookingPackage) => {
  if (!selectedBookingPackage) return [];

  const packages = [];

  if (selectedBookingPackage.two_hour_price_cents > 0) packages.push(createDurationOption(2, 'two_hour_price_cents'));
  if (selectedBookingPackage.three_hour_price_cents > 0)
    packages.push(createDurationOption(3, 'three_hour_price_cents'));
  if (selectedBookingPackage.half_day_cents > 0) packages.push(createDurationOption(4, 'half_day_cents'));
  if (selectedBookingPackage.six_hour_price_cents > 0) packages.push(createDurationOption(6, 'six_hour_price_cents'));
  if (selectedBookingPackage.all_day_cents > 0) packages.push(createDurationOption(8, 'all_day_cents'));

  return packages;
};

// New function to get available durations across all times for a date
export const getAvailableDurationsForDate = ({ startDate, calendarInfo, selectedPackage }) => {
  const calendarDay = startDate || dayjs().format('YYYY-MM-DD');
  const boatCalendar = calendarInfo;
  const details = boatCalendar?.[calendarDay];
  const updatedStartTimes = details?.updated_start_times as { available: boolean; [key: string]: any };

  if (!selectedPackage) return [];

  // If updatedStartTimes is empty, all start times are available
  // Return all boat options as available
  if (!updatedStartTimes || Object.keys(updatedStartTimes).length === 0) {
    return getBoatAvailableOptions(selectedPackage).map((option) => option.value);
  }

  const packageType = selectedPackage?.package_type;
  const allAvailableDurations = new Set();

  // Collect all available durations across all time slots
  Object.values(updatedStartTimes).forEach((timeInfo) => {
    if (timeInfo.available && timeInfo[`${packageType}_durations`]) {
      timeInfo[`${packageType}_durations`].forEach((duration) => {
        allAvailableDurations.add(duration);
      });
    }
  });

  return Array.from(allAvailableDurations).sort((a, b) => Number(a) - Number(b));
};

// Function to find the best available duration
export const findBestAvailableDuration = (currentDuration, availableDurations) => {
  if (!availableDurations.length) return null;

  // If current duration is available, keep it
  if (availableDurations.includes(currentDuration)) {
    return currentDuration;
  }

  // Find the closest lower duration
  const lowerDurations = availableDurations.filter((d) => d < currentDuration);
  if (lowerDurations.length > 0) {
    return Math.max(...lowerDurations);
  }

  // If no lower duration, return the lowest available
  return Math.min(...availableDurations);
};

// Function to calculate available length options based on start date, time, and calendar info
export const calculateAvailableLengthOptions = ({ startDate, startTime, calendarInfo, selectedPackage }) => {
  const boatOptions = getBoatAvailableOptions(selectedPackage);

  if (!startTime) {
    const availableDurationsForDate = getAvailableDurationsForDate({
      startDate,
      calendarInfo,
      selectedPackage,
    });

    return boatOptions.map((obj) => ({
      label: obj.label,
      value: obj.value,
      name: obj.name,
      available: availableDurationsForDate.includes(obj.value),
    }));
  }

  const calendarDay = startDate || dayjs().format('YYYY-MM-DD');
  const boatCalendar = calendarInfo;
  const details = boatCalendar?.[calendarDay];
  const updatedStartTimes = details?.updated_start_times;

  let boatOptionsAvailable = boatOptions.map((obj) => obj.value);

  if (updatedStartTimes && startTime) {
    const timeDateInfo = updatedStartTimes[startTime];

    if (timeDateInfo) {
      const packageType = selectedPackage?.package_type;
      const selectedPackageDurations = timeDateInfo[`${packageType}_durations`];
      if (timeDateInfo.available === true && !valueEmpty(selectedPackageDurations)) {
        boatOptionsAvailable = selectedPackageDurations;
      }
    }
  }

  const availableDurationsSelected = getBoatAvailableOptions(selectedPackage).map((obj) => ({
    label: obj.label,
    value: obj.value,
    name: obj.name,
    available: boatOptionsAvailable?.includes(obj.value),
  }));

  return availableDurationsSelected;
};
